import type { ChemicalActivitiesAndUses } from '../../types/questionnaires.ts';
import type { TRIChemicalActivitiesAndUsesDataType } from '../../types/form-r.ts';
import type { CasOrCategory, UsageTypeID } from '../../types/db.ts';
import { UsageTypeIds } from '../usage-types.ts';
import type { Warning } from '../../types/tri.ts';

function hasTruthyElements(object: Record<string, boolean | string[]>): boolean {
  for (const value of Object.values(object)) {
    if (value === true || (Array.isArray(value) && value.length > 0)) {
      return true;
    }
  }

  return false;
}

export function formRChemicalActivitiesAndUses(
  casOrCategory: CasOrCategory,
  usageTypeIds: Set<UsageTypeID>,
  uses: ChemicalActivitiesAndUses,
  warnings: Warning[],
): TRIChemicalActivitiesAndUsesDataType {
  const manufactured = usageTypeIds.has(UsageTypeIds.Manufactured);
  const processed = usageTypeIds.has(UsageTypeIds.Processed);
  const otherwiseUsed = usageTypeIds.has(UsageTypeIds['Otherwise Used']);

  const manufacturedSpecifics = {
    ChemicalUsedProcessedIndicator: uses.manufacture.onSiteUseOrProcessing,
    ChemicalSalesDistributionIndicator: uses.manufacture.saleOrDistribution,
    ChemicalByproductIndicator: uses.manufacture.byProduct,
    ChemicalManufactureImpurityIndicator: uses.manufacture.impurity,
  };

  const processedSpecifics = {
    ChemicalReactantIndicator: uses.process.reactantCategories.length > 0,
    ChemicalReactantSubcategory: uses.process.reactantCategories,
    ChemicalFormulationComponentIndicator: uses.process.formulationComponents.length > 0,
    ChemicalFormulationComponentSubcategory: uses.process.formulationComponents,
    ChemicalArticleComponentIndicator: uses.process.articleComponent,
    ChemicalRepackagingIndicator: uses.process.repackaging,
    ChemicalProcessImpurityIndicator: uses.process.impurity,
    ChemicalProcessRecyclingIndicator: uses.process.recycling,
  };

  const otherwiseUsedSpecifics = {
    ChemicalProcessingAidIndicator: uses.otherwiseUse.chemicalProcessingAid.length > 0,
    ChemicalProcessingAidSubcategory: uses.otherwiseUse.chemicalProcessingAid,
    ChemicalManufactureAidIndicator: uses.otherwiseUse.manufacturingAid.length > 0,
    ChemicalManufactureAidSubcategory: uses.otherwiseUse.manufacturingAid,
    ChemicalAncillaryUsageIndicator: uses.otherwiseUse.ancillaryOrOtherUse.length > 0,
    ChemicalAncillaryUsageSubcategory: uses.otherwiseUse.ancillaryOrOtherUse,
  };

  const result: TRIChemicalActivitiesAndUsesDataType = {
    ...(manufactured
      ? {
          ChemicalProducedIndicator: uses.manufacture.produce,
          ChemicalImportedIndicator: uses.manufacture.import,
          ...manufacturedSpecifics,
        }
      : {
          ChemicalProducedIndicator: false,
          ChemicalImportedIndicator: false,
          ChemicalUsedProcessedIndicator: false,
          ChemicalSalesDistributionIndicator: false,
          ChemicalByproductIndicator: false,
          ChemicalManufactureImpurityIndicator: false,
        }),

    ...(processed
      ? processedSpecifics
      : {
          ChemicalReactantIndicator: false,
          ChemicalReactantSubcategory: [],
          ChemicalFormulationComponentIndicator: false,
          ChemicalFormulationComponentSubcategory: [],
          ChemicalArticleComponentIndicator: false,
          ChemicalRepackagingIndicator: false,
          ChemicalProcessImpurityIndicator: false,
          ChemicalProcessRecyclingIndicator: false,
        }),

    ...(otherwiseUsed
      ? otherwiseUsedSpecifics
      : {
          ChemicalProcessingAidIndicator: false,
          ChemicalProcessingAidSubcategory: [],
          ChemicalManufactureAidIndicator: false,
          ChemicalManufactureAidSubcategory: [],
          ChemicalAncillaryUsageIndicator: false,
          ChemicalAncillaryUsageSubcategory: [],
        }),
  };

  if (manufactured && !uses.manufacture.produce && !uses.manufacture.import) {
    warnings.push({
      warningID: 'TW1003',
      type: 'questionnaire',
      subType: 'chemicalActivitiesAndUses',
      casOrCategory,
      message: `${casOrCategory} was marked as manufactured, but is missing whether it is produced and/or imported.`,
      blocksPriorYearSection8Calculation: false,
    });
  }

  if (manufactured && !hasTruthyElements(manufacturedSpecifics)) {
    warnings.push({
      warningID: 'TW1004',
      type: 'questionnaire',
      subType: 'chemicalActivitiesAndUses',
      casOrCategory,
      message: `${casOrCategory} was marked as manufactured, but exact uses were not chosen (impurity, byproduct, sale or distribution, on-site use).`,
      blocksPriorYearSection8Calculation: false,
    });
  }

  if (processed && !hasTruthyElements(processedSpecifics)) {
    warnings.push({
      warningID: 'TW1005',
      type: 'questionnaire',
      subType: 'chemicalActivitiesAndUses',
      casOrCategory,
      message: `${casOrCategory} was marked as processed, but exact uses were not chosen (reactant, formulation, etc.).`,
      blocksPriorYearSection8Calculation: false,
    });
  }

  if (otherwiseUsed && !hasTruthyElements(otherwiseUsedSpecifics)) {
    warnings.push({
      warningID: 'TW1006',
      type: 'questionnaire',
      subType: 'chemicalActivitiesAndUses',
      casOrCategory,
      message: `${casOrCategory} was marked as otherwise used, but exact uses were not chosen (chemical processing, manufacturing aid, etc.).`,
      blocksPriorYearSection8Calculation: false,
    });
  }

  return result;
}

export function formRChemicalActivitiesAndUsesIsComplete(
  casOrCategory: CasOrCategory,
  usageTypeIds: Set<UsageTypeID>,
  uses: ChemicalActivitiesAndUses,
) {
  const warnings: Warning[] = [];
  formRChemicalActivitiesAndUses(casOrCategory, usageTypeIds, uses, warnings);
  return warnings.length === 0;
}
